import React, { useEffect, useMemo, useState } from "react";
import {
  addInwardEntry,
  addInwardPayment,
  addSalesEntry,
  addSalesPayment,
  getApiBaseRoot,
  getCustomers,
  getInwardEntries,
  getSalesEntries,
  getSuppliers,
  saveReceiptHtml,
  updateInwardPayment,
  updateSalesPayment,
  updateSalesEntryPaymentStatus,
} from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import { getLogoDataUrl } from "../utils/receiptPrint";
import {
  buildLedgerPrintHtml,
  buildLedgerStatement,
  buildLedgerWhatsAppMessage,
  buildPartyLedgerSummaries,
  extractLedgerWhatsAppPhone,
  filterMainLedgerEntries,
  formatLedgerMoney,
  isLedgerOpeningBalanceEntry,
  LEDGER_CUSTOM_PAYMENT_MODE,
  LEDGER_OPENING_BALANCE_CATEGORY,
  LEDGER_OPENING_BALANCE_DATE,
  LEDGER_PAYMENT_MODES,
  LEDGER_WINDOW_DAYS,
  ledgerPartyName,
  localLedgerToday,
} from "./paymentLedgerModel";
import "./PaymentLedger.css";

function sumSummaries(rows) {
  return rows.reduce(
    (totals, row) => {
      totals.totalAmount += Number(row.totalAmount) || 0;
      totals.paidAmount += Number(row.paidAmount) || 0;
      totals.adjustmentAmount += Number(row.adjustmentAmount) || 0;
      totals.pendingAmount += Number(row.pendingAmount) || 0;
      totals.entryCount += Number(row.entryCount) || 0;
      return totals;
    },
    { totalAmount: 0, paidAmount: 0, adjustmentAmount: 0, pendingAmount: 0, entryCount: 0 }
  );
}

function makePaymentForm() {
  return { date: localLedgerToday(), amount: "", mode: "Cash", customMode: "" };
}

function entryDate(type, entry) {
  return toComparableYmd(type === "purchase" ? entry?.createdAt : entry?.saleDate);
}

async function printStatement(statement) {
  const printWindow = window.open("", "PAYMENT_LEDGER_PRINT", "height=850,width=1100");
  if (!printWindow) {
    alert("Please allow popups to print the payment ledger.");
    return;
  }

  const logoUrl = await getLogoDataUrl().catch(() => "");
  printWindow.document.write(buildLedgerPrintHtml(statement, logoUrl));
  printWindow.document.close();

  const images = Array.from(printWindow.document.images || []);
  const imageReady = Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        })
    )
  );
  await Promise.race([imageReady, new Promise((resolve) => setTimeout(resolve, 2000))]);
  printWindow.focus();
  printWindow.print();
}

function LedgerOverview({ title, tone, totals, paidLabel }) {
  return (
    <article className={`ledger-overview ledger-overview-${tone}`}>
      <div className="ledger-overview-heading">
        <span>{title}</span>
        <strong>{totals.entryCount} entries</strong>
      </div>
      <div className="ledger-overview-total">
        <small>Total Amount</small>
        <strong>Rs. {formatLedgerMoney(totals.totalAmount)}</strong>
      </div>
      <div className="ledger-overview-grid">
        <div><span>{paidLabel}</span><strong>Rs. {formatLedgerMoney(totals.paidAmount)}</strong></div>
        <div><span>Pending</span><strong>Rs. {formatLedgerMoney(totals.pendingAmount)}</strong></div>
      </div>
    </article>
  );
}

export default function PaymentLedger() {
  const [inwardEntries, setInwardEntries] = useState([]);
  const [salesEntries, setSalesEntries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activeType, setActiveType] = useState("purchase");
  const [partyFilters, setPartyFilters] = useState({ purchase: "", sales: "" });
  const [asOfDate, setAsOfDate] = useState(localLedgerToday);
  const [dateFilters, setDateFilters] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState(makePaymentForm);
  const [editingPayment, setEditingPayment] = useState(null);
  const [openingBalanceOpen, setOpeningBalanceOpen] = useState(false);
  const [openingBalanceSaving, setOpeningBalanceSaving] = useState(false);
  const [openingBalanceAmount, setOpeningBalanceAmount] = useState("");
  const [whatsAppSending, setWhatsAppSending] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const [purchases, sales, supplierList, customerList] = await Promise.all([
        getInwardEntries(),
        getSalesEntries(),
        getSuppliers(),
        getCustomers(),
      ]);
      setInwardEntries(Array.isArray(purchases) ? purchases : []);
      setSalesEntries(Array.isArray(sales) ? sales : []);
      setSuppliers(Array.isArray(supplierList) ? supplierList : []);
      setCustomers(Array.isArray(customerList) ? customerList : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load payment ledger data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const purchaseSummaries = useMemo(
    () => buildPartyLedgerSummaries("purchase", inwardEntries, asOfDate, suppliers),
    [inwardEntries, asOfDate, suppliers]
  );
  const salesSummaries = useMemo(
    () => buildPartyLedgerSummaries("sales", salesEntries, asOfDate, customers),
    [salesEntries, asOfDate, customers]
  );
  const purchaseTotals = useMemo(() => sumSummaries(purchaseSummaries), [purchaseSummaries]);
  const salesTotals = useMemo(() => sumSummaries(salesSummaries), [salesSummaries]);

  const activeEntries = activeType === "purchase" ? inwardEntries : salesEntries;
  const activeSummaries = activeType === "purchase" ? purchaseSummaries : salesSummaries;
  const activeParty = partyFilters[activeType];
  const activePartyKey = activeParty.toLocaleLowerCase();
  const activeOpeningEntry = activeEntries.find(
    (entry) =>
      isLedgerOpeningBalanceEntry(entry) &&
      ledgerPartyName(activeType, entry).toLocaleLowerCase() === activePartyKey
  );
  const activeStatement = useMemo(
    () =>
      activeParty
        ? buildLedgerStatement({
            type: activeType,
            entries: activeEntries,
            partyName: activeParty,
            asOfDate,
            fromDate: dateFilters.from,
            toDate: dateFilters.to,
          })
        : null,
    [activeType, activeEntries, activeParty, asOfDate, dateFilters.from, dateFilters.to]
  );
  const currentPaymentStatement = useMemo(
    () =>
      activeParty
        ? buildLedgerStatement({
            type: activeType,
            entries: activeEntries,
            partyName: activeParty,
            asOfDate: localLedgerToday(),
          })
        : null,
    [activeType, activeEntries, activeParty]
  );

  const setParty = (value) => {
    setPartyFilters((current) => ({ ...current, [activeType]: value }));
    setPaymentOpen(false);
    setEditingPayment(null);
    setOpeningBalanceOpen(false);
  };

  const switchLedger = (type) => {
    setActiveType(type);
    setPaymentOpen(false);
    setEditingPayment(null);
    setOpeningBalanceOpen(false);
  };

  const openPayment = () => {
    if (!activeParty || !currentPaymentStatement || currentPaymentStatement.pendingAmount <= 0) return;
    setAsOfDate(localLedgerToday());
    setEditingPayment(null);
    setPaymentForm(makePaymentForm());
    setPaymentOpen(true);
  };

  const closePayment = () => {
    if (paymentSaving) return;
    setPaymentOpen(false);
    setEditingPayment(null);
    setPaymentForm(makePaymentForm());
  };

  const openEditPayment = (payment) => {
    if (!payment?.paymentId) return;
    const storedMode = String(payment.mode || "").trim();
    const isStandardMode = LEDGER_PAYMENT_MODES.filter((mode) => mode !== LEDGER_CUSTOM_PAYMENT_MODE).includes(storedMode);
    setEditingPayment(payment);
    setPaymentForm({
      date: toComparableYmd(payment.date) || localLedgerToday(),
      amount: Number(payment.amount || 0).toFixed(2),
      mode: isStandardMode ? storedMode : LEDGER_CUSTOM_PAYMENT_MODE,
      customMode: isStandardMode ? "" : storedMode,
    });
    setPaymentOpen(true);
  };

  const openOpeningBalance = () => {
    if (!activeParty) return;
    if (activeOpeningEntry) {
      alert(`An opening balance has already been added for this ${activeType === "purchase" ? "supplier" : "customer"}.`);
      return;
    }
    setOpeningBalanceAmount("");
    setOpeningBalanceOpen(true);
  };

  const closeOpeningBalance = () => {
    if (openingBalanceSaving) return;
    setOpeningBalanceOpen(false);
    setOpeningBalanceAmount("");
  };

  const sendLedgerWhatsApp = async () => {
    if (!activeStatement || whatsAppSending) return;

    const partyKey = activeParty.toLocaleLowerCase();
    const masterList = activeType === "purchase" ? suppliers : customers;
    const master = masterList.find(
      (party) => String(party?.name || "").trim().toLocaleLowerCase() === partyKey
    );
    const snapshot = activeEntries.find(
      (entry) => ledgerPartyName(activeType, entry).toLocaleLowerCase() === partyKey
    );
    const contactText = activeType === "purchase"
      ? [master?.contact, master?.phone, master?.mobile, snapshot?.supplierPhone].filter(Boolean).join("\n")
      : [master?.phone, master?.contact, master?.mobile, snapshot?.customerPhone, snapshot?.customer]
          .filter(Boolean)
          .join("\n");
    const phone = extractLedgerWhatsAppPhone(contactText);

    if (!phone) {
      alert(`No valid ${activeType === "purchase" ? "supplier" : "customer"} contact number was found.`);
      return;
    }

    const whatsAppWindow = window.open("", "_blank");
    if (!whatsAppWindow) {
      alert("Please allow popups to send the WhatsApp ledger.");
      return;
    }

    whatsAppWindow.opener = null;
    whatsAppWindow.document.title = "Preparing WhatsApp ledger";
    whatsAppWindow.document.body.textContent = "Preparing the payment ledger. Please wait...";
    setWhatsAppSending(true);

    try {
      const logoUrl = await getLogoDataUrl().catch(() => "");
      const html = buildLedgerPrintHtml(activeStatement, logoUrl);
      const receipt = await saveReceiptHtml(html);
      let receiptLink = "";

      if (receipt?.url) {
        if (String(receipt.url).startsWith("http")) {
          receiptLink = String(receipt.url);
        } else {
          const root = getApiBaseRoot().replace(/\/$/, "");
          const raw = String(receipt.url);
          const tail = raw.includes("/receipts/")
            ? raw.substring(raw.indexOf("/receipts/"))
            : `/${raw.replace(/^\/+/, "")}`;
          receiptLink = `${root}${tail}`;
        }
      }

      if (!receiptLink) throw new Error("The ledger link could not be created.");
      const message = buildLedgerWhatsAppMessage(activeStatement, receiptLink);
      if (whatsAppWindow.closed) throw new Error("The WhatsApp tab was closed. Please try again.");
      whatsAppWindow.location.replace(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`);
    } catch (err) {
      console.error(err);
      if (!whatsAppWindow.closed) whatsAppWindow.close();
      alert(err?.message || "Unable to prepare the WhatsApp ledger.");
    } finally {
      setWhatsAppSending(false);
    }
  };

  const saveOpeningBalance = async (event) => {
    event.preventDefault();
    if (openingBalanceSaving || !activeParty) return;
    if (activeOpeningEntry) {
      alert(`An opening balance has already been added for this ${activeType === "purchase" ? "supplier" : "customer"}.`);
      setOpeningBalanceOpen(false);
      setOpeningBalanceAmount("");
      return;
    }

    const amount = +Number(openingBalanceAmount || 0).toFixed(2);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Enter a valid opening balance amount.");
      return;
    }

    setOpeningBalanceSaving(true);
    try {
      if (activeType === "purchase") {
        const payload = {
          supplierName: activeParty,
          category: LEDGER_OPENING_BALANCE_CATEGORY,
          numberOfBoxes: 0,
          createdAt: `${LEDGER_OPENING_BALANCE_DATE}T00:00:00.000Z`,
          total: amount,
          totalQty: 1,
          items: [{
            date: LEDGER_OPENING_BALANCE_DATE,
            material: LEDGER_OPENING_BALANCE_CATEGORY,
            qty: 1,
            rate: amount,
            amount,
            stockQty: 0,
            closingQty: 0,
            closingRate: 0,
            closingAmount: 0,
            retailQty: 0,
            retailRate: 0,
            retailAmount: 0,
          }],
        };
        await addInwardEntry(payload);
      } else {
        const master = customers.find(
          (customer) => String(customer?.name || "").trim().toLocaleLowerCase() === activePartyKey
        );
        const snapshot = activeEntries.find(
          (entry) => ledgerPartyName("sales", entry).toLocaleLowerCase() === activePartyKey
        );
        const customerPhone = String(master?.phone || snapshot?.customerPhone || "").trim();
        const customerAddress = String(master?.address || snapshot?.customerAddress || "").trim();
        const customerAddress2 = String(master?.address2 || snapshot?.customerAddress2 || "").trim();
        const payload = {
          category: LEDGER_OPENING_BALANCE_CATEGORY,
          saleDate: LEDGER_OPENING_BALANCE_DATE,
          customer: [activeParty, customerPhone, customerAddress, customerAddress2].filter(Boolean).join("\n"),
          customerName: activeParty,
          customerPhone,
          customerAddress,
          customerAddress2,
          source: LEDGER_OPENING_BALANCE_CATEGORY,
          transporterName: "",
          numberOfBoxes: 0,
          paymentDone: false,
          paymentType: "Credit",
          discountAmount: 0,
          items: [{
            material: LEDGER_OPENING_BALANCE_CATEGORY,
            qty: 1,
            rate: amount,
            amount,
            boxNumber: "",
            closingStock: 0,
          }],
        };
        await addSalesEntry(payload);
      }

      await refresh();
      setAsOfDate(localLedgerToday());
      setOpeningBalanceOpen(false);
      setOpeningBalanceAmount("");
      alert("Opening balance saved successfully.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Unable to save opening balance.");
    } finally {
      setOpeningBalanceSaving(false);
    }
  };

  const savePayment = async (event) => {
    event.preventDefault();
    if (paymentSaving || !activeParty) return;

    const amount = +Number(paymentForm.amount || 0).toFixed(2);
    const date = toComparableYmd(paymentForm.date);
    const today = localLedgerToday();
    const selectedMode = LEDGER_PAYMENT_MODES.includes(paymentForm.mode) ? paymentForm.mode : "Cash";
    const customMode = String(paymentForm.customMode || "").trim();
    const mode = selectedMode === LEDGER_CUSTOM_PAYMENT_MODE ? customMode : selectedMode;
    const available = Number(currentPaymentStatement?.pendingAmount) || 0;
    if (!date || date > today) {
      alert("Select a valid payment date.");
      return;
    }
    if (amount <= 0) {
      alert("Enter a valid payment amount.");
      return;
    }
    if (selectedMode === LEDGER_CUSTOM_PAYMENT_MODE && !customMode) {
      alert("Enter the payment mode.");
      return;
    }
    if (!editingPayment && amount > available + 0.0001) {
      alert(`Payment cannot exceed the pending amount of Rs. ${formatLedgerMoney(available)}.`);
      return;
    }

    if (editingPayment) {
      setPaymentSaving(true);
      try {
        const payload = { amount, date, mode, type: "payment" };
        if (activeType === "purchase") await updateInwardPayment(editingPayment.paymentId, payload);
        else await updateSalesPayment(editingPayment.paymentId, payload);
        await refresh();
        setPaymentOpen(false);
        setEditingPayment(null);
        setPaymentForm(makePaymentForm());
        alert("Payment updated successfully.");
      } catch (err) {
        console.error(err);
        await refresh();
        alert(err?.message || "Unable to update payment.");
      } finally {
        setPaymentSaving(false);
      }
      return;
    }

    const partyKey = activeParty.toLocaleLowerCase();
    const pendingEntries = filterMainLedgerEntries(activeType, activeEntries)
      .filter((entry) => ledgerPartyName(activeType, entry).toLocaleLowerCase() === partyKey)
      .map((entry) => ({
        entry,
        pendingAmount: buildLedgerStatement({
          type: activeType,
          entries: [entry],
          partyName: activeParty,
          asOfDate: today,
        }).pendingAmount,
      }))
      .filter((row) => row.pendingAmount > 0)
      .sort((a, b) => {
        const dateCompare = entryDate(activeType, a.entry).localeCompare(entryDate(activeType, b.entry));
        return dateCompare || String(a.entry?.id || "").localeCompare(String(b.entry?.id || ""));
      });

    let remaining = amount;
    let appliedCount = 0;
    setPaymentSaving(true);
    try {
      for (const row of pendingEntries) {
        if (remaining <= 0) break;
        const applied = +Math.min(remaining, row.pendingAmount).toFixed(2);
        if (applied <= 0) continue;
        const payload = { amount: applied, date, mode, type: "payment" };
        if (activeType === "purchase") await addInwardPayment(row.entry.id, payload);
        else {
          await addSalesPayment(row.entry.id, payload);
          if (!isLedgerOpeningBalanceEntry(row.entry) && applied >= row.pendingAmount - 0.0001) {
            await updateSalesEntryPaymentStatus(row.entry.id, true);
          }
        }
        remaining = +(remaining - applied).toFixed(2);
        appliedCount += 1;
      }
      if (remaining > 0.0001) throw new Error("Unable to allocate the full payment amount.");
      await refresh();
      setAsOfDate(today);
      setPaymentOpen(false);
      setPaymentForm(makePaymentForm());
      alert(`Payment saved successfully across ${appliedCount} ${appliedCount === 1 ? "entry" : "entries"}.`);
    } catch (err) {
      console.error(err);
      await refresh();
      alert(err?.message || "Unable to save payment.");
    } finally {
      setPaymentSaving(false);
    }
  };

  return (
    <div className="payment-ledger-page">
      <header className="payment-ledger-header">
        <div>
          <span className="payment-ledger-kicker">ARF SEAFOODS · ACCOUNTS</span>
          <h1>Payment Ledger</h1>
          <p>Supplier and customer balances with item-wise activity and payment history.</p>
        </div>
        <button type="button" className="ledger-refresh" onClick={refresh} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh Data"}
        </button>
      </header>

      {error ? <div className="ledger-error" role="alert">{error}</div> : null}

      <section className="ledger-overview-pair" aria-label="Purchase and sales ledger totals">
        <LedgerOverview title="Purchase Ledger" tone="purchase" totals={purchaseTotals} paidLabel="Amount Paid" />
        <LedgerOverview title="Sales Ledger" tone="sales" totals={salesTotals} paidLabel="Amount Received" />
      </section>

      <section className={`ledger-workspace ledger-workspace-${activeType}`}>
        <div className="ledger-tabs" role="tablist" aria-label="Payment ledger type">
          <button
            type="button"
            role="tab"
            aria-selected={activeType === "purchase"}
            className={activeType === "purchase" ? "active" : ""}
            onClick={() => switchLedger("purchase")}
          >
            Purchase Payments
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeType === "sales"}
            className={activeType === "sales" ? "active" : ""}
            onClick={() => switchLedger("sales")}
          >
            Sales Payments
          </button>
        </div>

        <div className="ledger-controls">
          <label className="ledger-field ledger-party-field">
            <span>{activeType === "purchase" ? "Supplier" : "Customer"}</span>
            <select value={activeParty} onChange={(event) => setParty(event.target.value)}>
              <option value="">All {activeType === "purchase" ? "suppliers" : "customers"}</option>
              {activeSummaries
                .slice()
                .sort((a, b) => a.partyName.localeCompare(b.partyName, undefined, { sensitivity: "base" }))
                .map((summary) => (
                  <option key={summary.partyName.toLocaleLowerCase()} value={summary.partyName}>
                    {summary.partyName} · Pending Rs. {formatLedgerMoney(summary.pendingAmount)}
                  </option>
                ))}
            </select>
          </label>
          <label className="ledger-field">
            <span>Statement As On</span>
            <input
              type="date"
              value={asOfDate}
              max={localLedgerToday()}
              onChange={(event) => setAsOfDate(event.target.value || localLedgerToday())}
            />
          </label>
          <label className="ledger-field">
            <span>From Date</span>
            <input
              type="date"
              value={dateFilters.from}
              max={dateFilters.to || localLedgerToday()}
              onChange={(event) => setDateFilters((current) => ({ ...current, from: event.target.value }))}
            />
          </label>
          <label className="ledger-field">
            <span>To Date</span>
            <input
              type="date"
              value={dateFilters.to}
              min={dateFilters.from || undefined}
              max={localLedgerToday()}
              onChange={(event) => setDateFilters((current) => ({ ...current, to: event.target.value }))}
            />
          </label>
          <div className="ledger-control-actions">
            <button
              type="button"
              className="ledger-date-clear"
              disabled={!dateFilters.from && !dateFilters.to}
              onClick={() => setDateFilters({ from: "", to: "" })}
            >
              Clear Date Range
            </button>
            <button
              type="button"
              className="ledger-opening-button"
              disabled={!activeParty || Boolean(activeOpeningEntry)}
              onClick={openOpeningBalance}
            >
              {activeOpeningEntry ? "Opening Balance Added" : "+ Opening Balance"}
            </button>
            <button
              type="button"
              className="ledger-payment-button"
              disabled={!activeParty || !currentPaymentStatement || currentPaymentStatement.pendingAmount <= 0}
              onClick={openPayment}
            >
              + Payment Entry
            </button>
            <button
              type="button"
              className="ledger-print-button"
              disabled={!activeStatement}
              onClick={() => activeStatement && printStatement(activeStatement)}
            >
              Print Statement
            </button>
            <button
              type="button"
              className="ledger-whatsapp-button"
              disabled={!activeStatement || whatsAppSending}
              onClick={sendLedgerWhatsApp}
            >
              {whatsAppSending ? "Preparing..." : "Send WhatsApp"}
            </button>
          </div>
        </div>

        {!activeParty ? (
          <div className="ledger-party-list">
            <div className="ledger-section-heading">
              <div>
                <span>{activeType === "purchase" ? "SUPPLIER-WISE" : "CUSTOMER-WISE"}</span>
                <h2>{activeType === "purchase" ? "Purchase balances" : "Sales balances"}</h2>
              </div>
              <small>Select a party to view and print the latest seven transaction dates.</small>
            </div>
            <div className="ledger-table-wrap">
              <table className="ledger-party-table">
                <thead><tr><th>{activeType === "purchase" ? "Supplier" : "Customer"}</th><th className="number">Entries</th><th className="number">Total Amount</th><th className="number">{activeType === "purchase" ? "Paid" : "Received"}</th><th className="number">Pending</th><th /></tr></thead>
                <tbody>
                  {activeSummaries.length ? activeSummaries.map((summary) => (
                    <tr key={summary.partyName.toLocaleLowerCase()}>
                      <td><strong>{summary.partyName}</strong></td>
                      <td className="number">{summary.entryCount}</td>
                      <td className="number">{formatLedgerMoney(summary.totalAmount)}</td>
                      <td className="number ledger-paid-text">{formatLedgerMoney(summary.paidAmount)}</td>
                      <td className="number ledger-pending-text">{formatLedgerMoney(summary.pendingAmount)}</td>
                      <td><button type="button" className="ledger-view-button" onClick={() => setParty(summary.partyName)}>View Ledger</button></td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} className="ledger-empty">No {activeType} ledger records found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="ledger-statement-panel">
            <div className="ledger-statement-heading">
              <div>
                <span>{activeStatement.partyLabel.toUpperCase()}</span>
                <h2>{activeStatement.partyName}</h2>
                <p>{activeStatement.rangeMode === "custom" ? "Selected date range" : `Latest ${LEDGER_WINDOW_DAYS} transaction dates`} · {formatDisplayDate(activeStatement.windowStart)} to {formatDisplayDate(activeStatement.windowEnd)}</p>
              </div>
              <button type="button" onClick={() => setParty("")}>View all parties</button>
            </div>

            <div className="ledger-statement-summary">
              <div><span>{activeStatement.amountLabel}</span><strong>Rs. {formatLedgerMoney(activeStatement.totalAmount)}</strong></div>
              <div><span>{activeStatement.paidLabel}</span><strong>Rs. {formatLedgerMoney(activeStatement.paidAmount)}</strong></div>
              <div><span>Adjustments</span><strong>Rs. {formatLedgerMoney(activeStatement.adjustmentAmount)}</strong></div>
              <div className="pending"><span>Amount Pending</span><strong>Rs. {formatLedgerMoney(activeStatement.pendingAmount)}</strong></div>
            </div>

            <div className="ledger-table-wrap">
              <table className="ledger-detail-table">
                <thead><tr><th>Date</th><th>Particulars</th><th className="number">Qty</th><th className="number">Rate</th><th className="number">Amount</th><th>Payment Mode</th><th className="number">Balance</th><th>Actions</th></tr></thead>
                <tbody>
                  <tr className="ledger-opening-row">
                    <td></td>
                    <td colSpan={5}>Previous Balance</td>
                    <td className="number">{formatLedgerMoney(activeStatement.openingBalance)}</td>
                    <td></td>
                  </tr>
                  {activeStatement.rows.length ? activeStatement.rows.map((row, index) => (
                    <tr key={`${row.date}-${row.kind}-${row.particulars}-${row.mode}-${index}`} className={`ledger-row-${row.kind}`}>
                      <td>{formatDisplayDate(row.date)}</td>
                      <td><strong>{row.particulars}</strong>{row.references.length ? <small>{row.references.length} reference{row.references.length === 1 ? "" : "s"}</small> : null}</td>
                      <td className="number">{row.kind === "item" ? Number(row.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 }) : ""}</td>
                      <td className="number">{row.kind === "item" && row.rate ? formatLedgerMoney(row.rate) : ""}</td>
                      <td className={`number${row.credit ? " ledger-paid-text" : ""}`}>{row.debit || row.credit ? formatLedgerMoney(row.debit || row.credit) : ""}</td>
                      <td>{row.mode || ""}</td>
                      <td className="number ledger-balance-text">{formatLedgerMoney(row.runningBalance)}</td>
                      <td className="ledger-action-cell">
                        <div className="ledger-action-buttons">
                          {(row.paymentRecords || []).length
                            ? row.paymentRecords.map((payment, paymentIndex) => (
                                <button
                                  key={payment.paymentId}
                                  type="button"
                                  className="ledger-edit-payment-button"
                                  aria-label={`Edit payment ${payment.reference || payment.paymentId}`}
                                  title={payment.reference ? `Edit payment for ${payment.reference}` : "Edit payment"}
                                  onClick={() => openEditPayment(payment)}
                                >
                                  {row.paymentRecords.length > 1 ? `Edit ${paymentIndex + 1}` : "Edit"}
                                </button>
                              ))
                            : ""}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={8} className="ledger-empty">No transactions up to the selected date.</td></tr>
                  )}
                  <tr className="ledger-closing-row"><td colSpan={6}>Closing Balance</td><td className="number">{formatLedgerMoney(activeStatement.pendingAmount)}</td><td></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {paymentOpen ? (
        <div className="ledger-payment-backdrop" onMouseDown={closePayment} role="presentation">
          <section className="ledger-payment-dialog" role="dialog" aria-modal="true" aria-labelledby="ledger-payment-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ledger-payment-heading">
              <div><span>{editingPayment ? "EDIT PAYMENT" : "PAYMENT ENTRY"}</span><h2 id="ledger-payment-title">{editingPayment ? (activeType === "purchase" ? "Edit Supplier Payment" : "Edit Customer Receipt") : (activeType === "purchase" ? "Pay Supplier" : "Receive from Customer")}</h2><p>{activeParty}</p></div>
              <button type="button" onClick={closePayment} disabled={paymentSaving}>Close</button>
            </div>
            <form className="ledger-payment-form" onSubmit={savePayment}>
              <div className="ledger-payment-pending"><span>{editingPayment ? "Recorded Amount" : "Current Pending"}</span><strong>Rs. {formatLedgerMoney(editingPayment?.amount || currentPaymentStatement?.pendingAmount || 0)}</strong></div>
              <label><span>Payment Date</span><input type="date" max={localLedgerToday()} value={paymentForm.date} onChange={(event) => setPaymentForm((current) => ({ ...current, date: event.target.value }))} required /></label>
              <label><span>Payment Mode</span><select value={paymentForm.mode} onChange={(event) => setPaymentForm((current) => ({ ...current, mode: event.target.value, customMode: event.target.value === LEDGER_CUSTOM_PAYMENT_MODE ? current.customMode : "" }))}>{LEDGER_PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select></label>
              {paymentForm.mode === LEDGER_CUSTOM_PAYMENT_MODE ? (
                <label className="wide"><span>Specify Payment Mode</span><input type="text" maxLength={30} value={paymentForm.customMode} onChange={(event) => setPaymentForm((current) => ({ ...current, customMode: event.target.value }))} placeholder="Enter payment mode" autoFocus required /></label>
              ) : null}
              <label className="wide"><span>Amount</span><input type="number" min="0.01" step="0.01" max={editingPayment ? undefined : currentPaymentStatement?.pendingAmount || undefined} value={paymentForm.amount} onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))} placeholder="Enter payment amount" required /></label>
              <div className="ledger-payment-actions"><button type="button" className="ledger-cancel-button" onClick={closePayment} disabled={paymentSaving}>Cancel</button><button type="submit" className="ledger-payment-button" disabled={paymentSaving}>{paymentSaving ? "Saving..." : editingPayment ? "Update Payment" : "Save Payment"}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      {openingBalanceOpen ? (
        <div className="ledger-payment-backdrop" onMouseDown={closeOpeningBalance} role="presentation">
          <section className="ledger-payment-dialog" role="dialog" aria-modal="true" aria-labelledby="ledger-opening-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ledger-payment-heading ledger-opening-heading">
              <div>
                <span>OPENING BALANCE</span>
                <h2 id="ledger-opening-title">
                  Add {activeType === "purchase" ? "Supplier" : "Customer"} Opening Balance
                </h2>
                <p>{activeParty}</p>
              </div>
              <button type="button" onClick={closeOpeningBalance} disabled={openingBalanceSaving}>Close</button>
            </div>
            <form className="ledger-payment-form" onSubmit={saveOpeningBalance}>
              <p className="ledger-opening-help">
                {activeType === "purchase"
                  ? "Enter the pending amount ARF Seafoods already owed this supplier before this application was started."
                  : "Enter the pending amount this customer already owed ARF Seafoods before this application was started."}
                {" "}This is a one-time entry and future payments will reduce it automatically.
              </p>
              <label className="wide">
                <span>Previous Pending Balance</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={openingBalanceAmount}
                  onChange={(event) => setOpeningBalanceAmount(event.target.value)}
                  placeholder="Enter opening balance amount"
                  autoFocus
                  required
                />
              </label>
              <div className="ledger-payment-actions">
                <button type="button" className="ledger-cancel-button" onClick={closeOpeningBalance} disabled={openingBalanceSaving}>Cancel</button>
                <button type="submit" className="ledger-opening-button" disabled={openingBalanceSaving}>
                  {openingBalanceSaving ? "Saving..." : "Save Opening Balance"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
